import React, { useEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { IS_MOBILE } from '../config';

// Renders a World Labs Marble interior (.spz Gaussian splat) inside r3f.
// Spark needs TWO objects in the scene: a SparkRenderer (fullscreen compositor
// mesh built from the WebGLRenderer) AND each SplatMesh. Without the renderer,
// splats load but draw nothing — the cause of the "no textures" bug.

function pickSplatUrl(splats) {
  if (!splats) return null;
  // Default 500k on ALL devices — the visual floor for rooms you actually stand
  // inside; the 100k tier reads blurry at eye height. Wow over bandwidth: phones
  // get the same tier as desktop (drop to ?splat=100k on a weak device, push to
  // ?splat=full_res on a strong one). full_res (~25MB) stays opt-in.
  const q = new URLSearchParams(window.location.search).get('splat');
  if (q && splats[q]) return splats[q];
  return splats['500k'] || splats.full_res || splats['150k'] || splats['100k'] || null;
}

// Splat files (.spz/.ply) use the Gaussian-splat convention of +Y DOWN; Spark
// preserves it, so an unrotated SplatMesh renders the room upside-down — the
// "damaged splat" bug. Flip 180° about X (Spark's own hello-world does this),
// then ground + center the room from percentile-trimmed splat centers so the
// floor sits at y=0 and the room surrounds the origin, matching where the
// camera rig and the overlay props expect to be. Percentiles (not the raw
// bounding box) keep stray fuzz splats — sky through a doorway, floaters —
// from sinking or shifting the whole room.
function fitSplatToScene(mesh, semantics, scaleOverride) {
  mesh.quaternion.set(1, 0, 0, 0); // 180° about X: local (x,y,z) → world (x,−y,−z)

  // Draft Marble worlds ship no metric_scale_factor (semantics=null) and render
  // at raw units — sometimes doll-house small. metric_scale_factor in the
  // manifest sizes them; ?scale=<n> overrides it live for tuning.
  const scale = Number.isFinite(scaleOverride)
    ? scaleOverride
    : Number.isFinite(semantics?.metric_scale_factor)
      ? semantics.metric_scale_factor
      : 1;
  mesh.scale.setScalar(scale);

  const xs = [], ys = [], zs = [];
  let i = 0;
  mesh.forEachSplat((_idx, center, _scales, _quat, opacity) => {
    // Sample every 3rd visible splat — plenty for robust stats, cheap on full_res.
    if (i++ % 3 !== 0 || opacity < 0.2) return;
    xs.push(center.x); ys.push(center.y); zs.push(center.z);
  });
  if (!xs.length) return;

  const pct = (arr, p) => {
    const a = [...arr].sort((m, n) => m - n);
    return a[Math.min(a.length - 1, Math.floor(p * a.length))];
  };

  // Local +Y is down, so the floor is the HIGH end of local Y (→ world minY).
  const floorLocalY = Number.isFinite(semantics?.ground_plane_offset)
    ? semantics.ground_plane_offset
    : pct(ys, 0.98);

  mesh.position.set(
    -pct(xs, 0.5) * scale,          // center the room on x=0
    floorLocalY * scale,            // floor → y=0
    pct(zs, 0.5) * scale            // center on z=0 (z negates through the flip)
  );

  // Room bounds in world space (percentile-trimmed so exterior fuzz doesn't
  // inflate them), pulled inward so the camera clamp keeps the viewer off the
  // walls where splat quality collapses. World transform: x' = s·x + tx,
  // y' = −s·y + ty, z' = −s·z + tz (the 180° X-flip negates y and z).
  const t = mesh.position;
  const inset = 0.92; // keep the camera to the inner ~92% of the room (roomy;
                      // was 0.82 — too tight to orbit/pan around to look behind)
  const x0 = pct(xs, 0.05) * scale + t.x, x1 = pct(xs, 0.95) * scale + t.x;
  const z0 = -pct(zs, 0.95) * scale + t.z, z1 = -pct(zs, 0.05) * scale + t.z;
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  const ceil = -pct(ys, 0.02) * scale + t.y;
  return {
    min: [cx + (x0 - cx) * inset, 0.35, cz + (z0 - cz) * inset],
    max: [cx + (x1 - cx) * inset, Math.max(1.8, ceil - 0.35), cz + (z1 - cz) * inset]
  };
}

// ── SPARK IS LOADED ON DEMAND ────────────────────────────────────────────────
// @sparkjsdev/spark is 4.9MB of JavaScript (its WASM ships inlined) — two thirds
// of the whole app bundle, measured 2026-09-23 — and it is only ever needed
// inside a Gaussian-splat room. Imported statically, every visitor downloaded
// and parsed it before the landing page could even appear. Now it is its own
// chunk, fetched when a splat room first mounts, and warmed in idle time once
// the player is out on the street (prefetchSpark) so the first room still
// opens without a wait.
let sparkP = null;
export function loadSpark() {
  if (!sparkP) sparkP = import('@sparkjsdev/spark').catch((e) => { sparkP = null; throw e; });
  return sparkP;
}
export function prefetchSpark() { loadSpark().catch(() => { /* the room will retry */ }); }

// Module-level SparkRenderer, recreated if the WebGL context changes (canvas
// remount / HMR) — a renderer bound to a dead context draws garbage.
let sparkRenderer = null;
function ensureSparkRenderer(gl, scene, SparkRenderer) {
  if (!sparkRenderer || sparkRenderer.renderer !== gl) {
    sparkRenderer?.parent?.remove(sparkRenderer);
    sparkRenderer = new SparkRenderer({ renderer: gl });
    sparkRenderer.frustumCulled = false;
  }
  if (sparkRenderer.parent !== scene) scene.add(sparkRenderer);
  return sparkRenderer;
}

export default function SplatWorld({ manifestUrl, onLoaded, onError, children }) {
  const { gl, scene } = useThree();
  const [manifest, setManifest] = useState(null);
  const meshRef = useRef();

  useEffect(() => {
    let cancelled = false;
    fetch(manifestUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`manifest ${r.status}`))))
      .then((m) => { if (!cancelled) setManifest(m); })
      .catch((e) => { if (!cancelled) onError?.(e); });
    return () => { cancelled = true; };
  }, [manifestUrl]);

  useEffect(() => {
    const url = pickSplatUrl(manifest?.splats);
    if (!url) return undefined;

    let cancelled = false;
    let mesh = null;
    loadSpark().then(({ SplatMesh, SparkRenderer }) => {
      if (cancelled) return;
      ensureSparkRenderer(gl, scene, SparkRenderer);
      mesh = new SplatMesh({
        url,
        onLoad: (m) => {
          if (cancelled) return;
          const sq = parseFloat(new URLSearchParams(window.location.search).get('scale'));
          const bounds = fitSplatToScene(m, manifest?.semantics, sq);
          if (bounds) useWorldStore.getState().setRoomBounds(bounds);
          // The as-fitted transform, published so a room's SOLID twin (the
          // Marble collider mesh — RoomCollider) can be mounted in the exact
          // same frame. Position/scale only: the 180°-X flip is the caller's
          // decision, because a GLB may arrive pre-flipped (+Y-up by spec)
          // while the splat never does.
          onLoaded?.(manifest, { position: m.position.toArray(), scale: m.scale.x });
        }
      });
      mesh.initialized?.catch?.((e) => { if (!cancelled) onError?.(e); });
      meshRef.current = mesh;
      scene.add(mesh);
    }).catch((e) => { if (!cancelled) onError?.(e); });

    return () => {
      cancelled = true;
      try { if (mesh) { scene.remove(mesh); mesh.dispose?.(); } } catch (_) {}
      meshRef.current = null;
      useWorldStore.getState().setRoomBounds(null);
      // Mobile: also tear down the SparkRenderer — its GPU buffers are sized to
      // the splat and otherwise LINGER after the SplatMesh is gone. Leaving the
      // room reloads the 24MB city GLBs, and that overlap OOM-crashes iOS a few
      // seconds after exit. Recreated on next entry via ensureSparkRenderer.
      if (IS_MOBILE && sparkRenderer) {
        try { sparkRenderer.parent?.remove(sparkRenderer); sparkRenderer.dispose?.(); } catch (_) {}
        sparkRenderer = null;
      }
    };
  }, [manifest, gl, scene]);

  return <group>{children}</group>;
}
